import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { io } from 'socket.io-client';
import { messageToastType_const } from 'src/app/constant/config';
import { showGlobalToast } from 'src/features/uiSlice';
import { MAX_LOG_LENGTH } from './constants';
import { getSocketOrigin } from './utils';

// Kết nối socket, subscribe MQTT theo serial đang xem, trả về trạng thái kết nối + log bản tin.
// onPeriodicPayload: nhận payload PERIODIC để cập nhật dữ liệu xe realtime.
const useMqttSocket = (serial, onPeriodicPayload) => {
  const dispatch = useDispatch();
  const [socketConnected, setSocketConnected] = useState(false);
  const [messages, setMessages] = useState([]);

  const socketRef = useRef(null);
  const currentSerialRef = useRef(null);
  const onPeriodicRef = useRef(onPeriodicPayload);
  onPeriodicRef.current = onPeriodicPayload;

  useEffect(() => {
    const socket = io(getSocketOrigin(), {
      path: '/api/socket.io',
      transports: ['websocket']
    });
    socketRef.current = socket;

    socket.on('connect', () => setSocketConnected(true));
    socket.on('disconnect', () => setSocketConnected(false));

    socket.on('mqtt:subscribed', ({ serial: subscribedSerial } = {}) => {
      // eslint-disable-next-line no-console
      console.log('[mqtt] subscribed', subscribedSerial);
    });

    socket.on('mqtt:message', msg => {
      if (!currentSerialRef.current || msg?.serial !== currentSerialRef.current) {
        return;
      }

      setMessages(prev => [{ ...msg, _id: `${msg.ts}-${prev.length}` }, ...prev].slice(0, MAX_LOG_LENGTH));

      if (msg.type === 'PERIODIC' && msg.payload && typeof msg.payload === 'object') {
        onPeriodicRef.current?.(msg.payload);
      }

      if (msg.type === 'FEEDBACK') {
        dispatch(
          showGlobalToast({
            message: `Thiết bị phản hồi lệnh: ${
              typeof msg.payload === 'string' ? msg.payload : JSON.stringify(msg.payload)
            }`,
            type: messageToastType_const.info
          })
        );
      }
    });

    socket.on('mqtt:error', ({ message } = {}) => {
      dispatch(
        showGlobalToast({
          message: message || 'Lỗi kết nối MQTT',
          type: messageToastType_const.error
        })
      );
    });

    return () => {
      if (currentSerialRef.current) {
        socket.emit('mqtt:unsubscribe', { serial: currentSerialRef.current });
        currentSerialRef.current = null;
      }
      socket.disconnect();
      socketRef.current = null;
    };
  }, [dispatch]);

  useEffect(() => {
    const socket = socketRef.current;
    const newSerial = serial || null;
    const oldSerial = currentSerialRef.current;

    if (!socket || newSerial === oldSerial) {
      return;
    }

    if (oldSerial) {
      socket.emit('mqtt:unsubscribe', { serial: oldSerial });
    }
    setMessages([]);
    currentSerialRef.current = newSerial;
    if (newSerial) {
      socket.emit('mqtt:subscribe', { serial: newSerial });
    }
  }, [serial]);

  return { socketConnected, messages };
};

export default useMqttSocket;
